import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

export type TimeFormat = '12h' | '24h' | 'browser';
export type NumberFormat = 'en-US' | 'de-DE' | 'fr-FR' | 'browser';
export type DateFormat = 'iso' | 'us' | 'eu' | 'short' | 'medium' | 'long' | 'full';

export interface UserSettings {
  useCustomNumberFormat: boolean;
  numberFormat: NumberFormat;

  useCustomDateFormat: boolean;
  timeFormat: TimeFormat;
  dateFormat: DateFormat;
}

const STORAGE_KEY = 'user_settings';

function getDefaultSettings(): UserSettings {
  return {
    useCustomNumberFormat: false,
    numberFormat: 'browser',
    useCustomDateFormat: false,
    timeFormat: 'browser',
    dateFormat: 'medium',
  };
}

function loadSettingsFromStorage(): UserSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...getDefaultSettings(), ...parsed };
    }
  } catch (error) {
    console.error('Failed to load settings from storage:', error);
  }
  return getDefaultSettings();
}

export const useSettingsStore = defineStore('settings', () => {
  const settings = ref<UserSettings>(loadSettingsFromStorage());

  const activeNumberFormat = computed(() => {
    if (!settings.value.useCustomNumberFormat || settings.value.numberFormat === 'browser') {
      return getBrowserLocale();
    }
    return settings.value.numberFormat;
  });

  const activeTimeFormat = computed((): '12h' | '24h' => {
    if (!settings.value.useCustomDateFormat || settings.value.timeFormat === 'browser') {
      return getBrowserTimeFormat();
    }
    return settings.value.timeFormat;
  });

  const activeDateFormat = computed(() => {
    if (!settings.value.useCustomDateFormat) {
      return 'medium' as DateFormat;
    }
    return settings.value.dateFormat;
  });

  function getBrowserLocale(): string {
    return navigator.language || 'en-US';
  }

  function getBrowserTimeFormat(): '12h' | '24h' {
    const locale = navigator.language || 'en-US';
    const use12Hour = locale.startsWith('en-US') || locale.startsWith('en-CA');
    return use12Hour ? '12h' : '24h';
  }

  function updateSettings(newSettings: Partial<UserSettings>) {
    settings.value = { ...settings.value, ...newSettings };
    saveToStorage();
  }

  function setNumberFormat(format: NumberFormat, useCustom: boolean = true) {
    settings.value.numberFormat = format;
    settings.value.useCustomNumberFormat = useCustom;
    saveToStorage();
  }

  function setTimeFormat(format: TimeFormat, useCustom: boolean = true) {
    settings.value.timeFormat = format;
    settings.value.useCustomDateFormat = useCustom;
    saveToStorage();
  }

  function setDateFormat(format: DateFormat) {
    settings.value.dateFormat = format;
    settings.value.useCustomDateFormat = true;
    saveToStorage();
  }

  function toggleCustomNumberFormat() {
    settings.value.useCustomNumberFormat = !settings.value.useCustomNumberFormat;
    saveToStorage();
  }

  function toggleCustomDateFormat() {
    settings.value.useCustomDateFormat = !settings.value.useCustomDateFormat;
    saveToStorage();
  }

  function resetToDefaults() {
    settings.value = getDefaultSettings();
    saveToStorage();
  }

  function saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings.value));
    } catch (error) {
      console.error('Failed to save settings to storage:', error);
    }
  }

  return {
    settings,
    activeNumberFormat,
    activeTimeFormat,
    activeDateFormat,
    updateSettings,
    setNumberFormat,
    setTimeFormat,
    setDateFormat,
    toggleCustomNumberFormat,
    toggleCustomDateFormat,
    resetToDefaults,
    saveToStorage,
    getBrowserLocale,
    getBrowserTimeFormat,
  };
});
